import * as React from 'react';
import LocalDiv from '../localization/LocalDiv';
import { FLEX_SPACED, FLEX_ROW, FLEX_COLUMN, FLEX_CENTERED } from '../layout/flexBox';
import { css } from '@emotion/core';
import ProgressCircle from '../loading/ProgressCircle';
import { COLORS, CHART_COLORS } from '../globalStyle/colors';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaste } from '@fortawesome/free-solid-svg-icons';
import { onMobileDevice } from '../globalStyle/mediaQueries';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import { ParticipantProject } from './models';
import { Link } from 'react-router-dom';
import { Routes } from '../navigation/routes';

import goalFlagGreenImg from '../assets/goal_flag_green.svg';
import goalFlagBlueImg from '../assets/goal_flag_blue.svg';
import chartImg from '../assets/chart.svg';
import processingImg from '../assets/processing.svg';


const styles = {
  root:           css`
    flex-wrap: wrap;
  `,
  linkLayout:     [
    FLEX_COLUMN,
    FLEX_CENTERED,
    css`
      margin: auto;
      text-decoration: none;
      
      &:hover { 
        opacity: .6;
      }
      
      ${onMobileDevice}{ 
        margin-bottom: 20px;
      }
    `,
  ],
  linkDisabled:   css`
    cursor: default;
    opacity: .6;
  `,
  stepTitle:      css`
    font-size: 13px;
    color: ${COLORS.DARK_GREY};
    font-weight: bold;
    margin-bottom: 5px;
  `,
  statusTitle:    css`
    color: ${COLORS.DARK_GREY};
  `,
  img:            css`
    width: 35px;
    height: 35px;
  `,
  processImg:     css`
    width: 75px;
    height: 75px;
  `,
  icon:           css`
    font-size: 30px;
  `,
  iconDisabled:   css`
    color: ${COLORS.WHITE}
  `,
  iconInProgress: css`
    color: ${COLORS.GREEN}
  `,
  iconComplete:   css`
    color: ${COLORS.BLUE}
  `,
  circle:         css`
    width: 130px;
    height: 130px;
    font-size: 30px;
    margin-bottom: 5px;
  `,
  nonChartCircle: css`
    width: 124px;
    height: 124px;
    margin-top: 3px;
    margin-bottom: 8px;
    border-radius: 100%;
    flex-shrink: 0;
  `,
};


const colorForCompletion = {
  chart: (isCompleted: boolean): string => isCompleted ? COLORS.BLUE : COLORS.GREEN,
};

const msgFor = (
  completed: boolean,
  progress: number,
  completedMsg: Str = Str.Completed,
): Str => completed ? completedMsg : progress === 0 ? Str.NotStarted : Str.InProgress;


type Props = {
  routes: Routes;
  proj: ParticipantProject | undefined;
  className?: string;
};


/**
 * ProjectSteps: a component that renders the middle step circles from the dashboard
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ProjectSteps: React.FC<Props> = props => {
  const project = props.proj;

  const selfSurveyStatus = React.useMemo(
    () => msgFor(!!props.proj?.selfSurveyCompleted, props.proj?.selfSurveyAmountComplete ?? 0, Str.Submitted),
    [props.proj],
  );

  const selfSurveyDisabled = React.useMemo(
    () =>  props.proj?.selfSurveyCompleted
           || props.proj?.passkeyExpired
           || (props.proj?.followupSurveyStep && !props.proj?.followupSurveyCreated),
    [props.proj],
  );
  const reportAvailable  = React.useMemo(() => props.proj?.reportAvailable, [props.proj]);
  const reportProcessing  = React.useMemo(() => props.proj?.reportProcessing, [props.proj]);

  const setGoalsStatus = React.useMemo(
    () => msgFor(props.proj?.setGoalsAmountComplete === 1, props.proj?.setGoalsAmountComplete ?? 0),
    [props.proj],
  );

  if (!props.proj) {
    return <div className={props.className} />;
  }

  return (
    <LocalDiv css={[FLEX_ROW, FLEX_SPACED, styles.root]} className={props.className}>
      {/* Self Survey */}
      {
        props.proj.selfSurveyStep && (
          <Link
            to={selfSurveyDisabled || !project?.courseAllowSelfSurvey ? '#' : props.routes.SelfSurvey}
            css={[
              styles.linkLayout,
              (selfSurveyDisabled || !project?.courseAllowSelfSurvey) && styles.linkDisabled,
            ]}
          >
            <Msg str={Str.SelfSurvey} css={styles.stepTitle} />
            <ProgressCircle
              ringConfig={[
                {
                  name:     'Self Survey',
                  progress: props.proj.selfSurveyCompleted ? 1 : props.proj.selfSurveyAmountComplete,
                  color:    colorForCompletion.chart(props.proj.selfSurveyCompleted),
                },
              ]}
              radius={62}
              stroke={12}
              insideColor={props.proj.selfSurveyCompleted ? COLORS.BLUE : COLORS.WHITE}
              css={[styles.circle, css`opacity: ${selfSurveyDisabled ? .6 : 1}`]}
            >
              <FontAwesomeIcon
                icon={faPaste}
                css={[
                  styles.icon,
                  styles.iconInProgress,
                  css`color: ${props.proj.selfSurveyCompleted ? COLORS.WHITE : COLORS.GREEN};`,
                ]}
              />
            </ProgressCircle>
            <Msg
              str={selfSurveyStatus}
              css={[
                styles.statusTitle,
                css`color: ${colorForCompletion.chart(props.proj.selfSurveyCompleted)};`,
              ]}
            />
          </Link>
        )
      }

      {/* Set Goals */}
      {
        props.proj.setGoalsStep && (
          <Link
            to={project?.courseAllowSetGoals ? props.routes.SetGoals : '#'}
            css={[styles.linkLayout, !project?.courseAllowSetGoals && styles.linkDisabled]}
          >
            <Msg str={Str.SetGoals} css={styles.stepTitle} />
            <ProgressCircle
              ringConfig={[
                {
                  name:     'Set Goals',
                  progress: props.proj.setGoalsAmountComplete,
                  color:    colorForCompletion.chart(props.proj.setGoalsAmountComplete === 1),
                },
              ]}
              radius={62}
              stroke={12}
              css={styles.circle}
            >
              <img
                src={props.proj.setGoalsAmountComplete === 1 ? goalFlagBlueImg : goalFlagGreenImg} alt='Flag'
                css={[styles.icon, styles.img]}
              />
            </ProgressCircle>
            <Msg
              str={setGoalsStatus}
              css={[
                styles.statusTitle,
                css`color: ${colorForCompletion.chart(props.proj.setGoalsAmountComplete === 1)};`,
              ]}
            />
          </Link>
        )
      }

      {/* View Report */}
      {
        props.proj.viewReportStep && (
          <Link
            to={reportAvailable && project?.courseAllowViewReport ? props.routes.FeedbackReport : '#'}
            css={[
              styles.linkLayout, 
              (!reportAvailable || reportProcessing || !project?.courseAllowViewReport) && styles.linkDisabled,
            ]}
          >
            <Msg str={Str.ViewReport} css={styles.stepTitle} />
            <div
              css={[
                FLEX_COLUMN,
                FLEX_CENTERED,
                styles.circle,
                styles.nonChartCircle,
                css`
                  background-color: ${
                    reportProcessing
                    ? COLORS.GREEN
                      :reportAvailable
                      ? COLORS.BLUE
                      : CHART_COLORS.RING_BG
                  };
                  opacity: ${reportAvailable || reportProcessing ? 1 : .6};
                `,
              ]}
            >
              <img 
                src={reportProcessing ? processingImg : chartImg}
                alt='Chart'
                css={[styles.icon, reportProcessing ? styles.processImg : styles.img]}
              />
            </div>
            <Msg
              str={ reportProcessing
                    ? Str.Generating
                    : reportAvailable
                      ? Str.Available
                      : Str.NotAvailable
                  }
              css={styles.statusTitle}
            />
          </Link>
        )
      }
    </LocalDiv>
  );
};

export default ProjectSteps;
